import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  FloorMapSyncHandler,
  SyncFloorMapParams
} from '../handlers/FloorMapSyncHandler'

export interface UseSyncFloorMapProps
  extends Omit<SyncFloorMapParams, 'mapInstance'> {
  mapContainerRef: any
  isReady?: boolean
}

/**
 * useSyncFloorMap Hook
 * Connects React lifecycle to FloorMapSyncHandler for in-place floor map synchronization.
 */
export function useSyncFloorMap({
  imagePath,
  isUnregisteredMap,
  mapContainerRef,
  setImgBounds,
  setImageLoading,
  maxBoundsPadding,
  onErrorFallback,
  isReady = true
}: UseSyncFloorMapProps) {
  const [isMapReady, setIsMapReady] = useState(false)
  const handler = useMemo(() => new FloorMapSyncHandler(), [])

  const handleSyncMapOnImageChange = useCallback(
    async (explicitMapInstance?: any) => {
      if (!imagePath) {
        setIsMapReady(false)
        return
      }

      setIsMapReady(false)
      setImageLoading?.(true)

      await handler.handleSyncMapOnImageChange({
        imagePath,
        isUnregisteredMap,
        mapInstance: explicitMapInstance || mapContainerRef,
        setImgBounds,
        setImageLoading,
        maxBoundsPadding,
        onErrorFallback
      })

      setIsMapReady(true)
    },
    [
      handler,
      imagePath,
      isUnregisteredMap,
      mapContainerRef,
      setImgBounds,
      setImageLoading,
      maxBoundsPadding,
      onErrorFallback
    ]
  )

  useEffect(() => {
    if (isReady) {
      handleSyncMapOnImageChange()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagePath, isUnregisteredMap, isReady])

  return {
    handler,
    handleSyncMapOnImageChange,
    isMapReady
  }
}

export default useSyncFloorMap
